// The login-code channel readback + the code EXCHANGE of ihjizli-otp, for the
// web onboarding (public, unauthenticated — the person is mid-signup).
//
//   GET  /api/otp/v1/auth/otp/channel?phone=<E.164>&country=IQ
//        → 200 {channel:'whatsapp'|'telegram'|'sms', sent_at, exchange?:true}
//        · 404 no send on record · 400/422/429 · 5xx
//   POST /api/otp/v1/auth/otp/exchange {phone, code, country:'IQ'}
//        → 200 {code:<the Authentik code>}                (Asiacell validated what was typed)
//        · 400 wrong_code (+ attempts_left) · 410 expired · 423 locked (detail LOCKED:<s>)
//        · 404 no_exchange · 429 rate_limited · 502 provider_unavailable
//
// WHY an exchange exists: for an Asiacell number the SMS carries ASIACELL's
// code (its API generates and verifies it), while Authentik verifies its OWN
// code. ihjizli-otp validates what the person typed with Asiacell and answers
// with the Authentik code; the enrollment then submits THAT to Authentik, so
// the flow, the lockout ladder and every screen after it are unchanged.
//
// This module stays free of lib/api.ts on purpose: api.ts imports the auth
// facade (refresh/lock), so importing it back from lib/auth would be a cycle,
// and these two calls must NOT touch the offline/maintenance stores or send a
// bearer (a readback that fails is a decoration lost, never "you are offline").

import { API_BASE } from '../env';
import { lockedMessage } from './authentik_flow';
import { AuthError, AUTH_COPY, parseLocked } from './types';

/** The channel words ihjizli-otp can name (`sms.WireChannel`) AND this build has copy for. */
export const OTP_CHANNELS = ['whatsapp', 'telegram', 'sms'] as const;
export type OtpChannel = (typeof OTP_CHANNELS)[number];

export interface OtpChannelInfo {
  /** null = the server named a channel this build has no copy for. */
  channel: OtpChannel | null;
  /** The last send went through an exchange channel (Asiacell): the typed code must be exchanged. */
  exchange: boolean;
}

export interface OtpClient {
  /** The readback. NEVER throws: every failure (404, 429, 5xx, transport, bad body) is null. */
  channel(phone: string): Promise<OtpChannelInfo | null>;
  /**
   * Trade the code the person typed for the Authentik code. Resolves null when
   * the last send was not an exchange (404 — submit the typed code directly).
   * Throws AuthError: invalid_code (attemptsLeft) · code_expired · locked
   * (retryAfterSeconds) · network (502 / transport) · unexpected (429 …).
   */
  exchange(phone: string, code: string): Promise<string | null>;
}

const TIMEOUT_MS = 5000;
const COUNTRY = 'IQ';
const LOCK_FLOOR_SECONDS = 60;

async function call(path: string, init: RequestInit = {}): Promise<Response> {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), TIMEOUT_MS);
  try {
    return await fetch(`${API_BASE}/otp/v1/auth/otp/${path}`, {
      ...init,
      headers: { Accept: 'application/json, application/problem+json', ...(init.body ? { 'Content-Type': 'application/json' } : {}) },
      signal: ctl.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

async function jsonOf(res: Response): Promise<Record<string, unknown>> {
  try {
    const b = (await res.json()) as unknown;
    return b && typeof b === 'object' ? (b as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

export const httpOtpClient: OtpClient = {
  async channel(phone) {
    try {
      const q = new URLSearchParams({ phone, country: COUNTRY });
      const res = await call(`channel?${q.toString()}`);
      if (res.status !== 200) return null;
      const body = await jsonOf(res);
      const word = typeof body.channel === 'string' ? body.channel : '';
      const channel = (OTP_CHANNELS as readonly string[]).includes(word) ? (word as OtpChannel) : null;
      const exchange = body.exchange === true;
      if (!channel && !exchange) return null;
      return { channel, exchange };
    } catch {
      return null;
    }
  },

  async exchange(phone, code) {
    let res: Response;
    try {
      res = await call('exchange', { method: 'POST', body: JSON.stringify({ phone, code, country: COUNTRY }) });
    } catch {
      throw new AuthError('network', AUTH_COPY.network);
    }
    const body = await jsonOf(res);
    if (res.status === 200) {
      if (typeof body.code !== 'string' || !body.code) throw new AuthError('unexpected', AUTH_COPY.unexpected);
      return body.code;
    }
    const detail = typeof body.detail === 'string' && body.detail ? body.detail : AUTH_COPY.unexpected;
    switch (res.status) {
      case 404:
        // `no_exchange` — and a server/proxy that has no such route at all
        // (older deploy): either way the typed code goes straight to Authentik.
        return null;
      case 400: {
        const left = typeof body.attempts_left === 'number' ? body.attempts_left : undefined;
        throw new AuthError('invalid_code', AUTH_COPY.invalidCode, undefined, left);
      }
      case 410:
        throw new AuthError('code_expired', AUTH_COPY.codeExpired);
      case 423: {
        const secs = Math.max(parseLocked(body.detail) ?? LOCK_FLOOR_SECONDS, 1);
        throw new AuthError('locked', lockedMessage(secs), secs);
      }
      default:
        if (res.status >= 500) throw new AuthError('network', AUTH_COPY.network);
        throw new AuthError('unexpected', detail);
    }
  },
};
